import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { ReactComponent as SelectorIcon } from '../../../assets/Selector.svg';
import { ReactComponent as AiStarIcon } from '../../../assets/Ai_Star.svg';
import { ReactComponent as TextIcon } from '../../../assets/Text.svg';
import { Play, Pause, Paintbrush } from 'lucide-react';
import { useCanvas } from '../../../hooks/useCanvas';
import AiDialog from './AiDialog';
import AgColorPicker from '../../UI/AgColorPicker';



const CanvasTools = ({ activeTool, onToolChange }) => {
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [isCanvasColorPickerOpen, setIsCanvasColorPickerOpen] = useState(false);
  const [canvasPickerPosition, setCanvasPickerPosition] = useState({ top: 0, left: 0 });
  const aiButtonRef = useRef(null);
  const paintbrushButtonRef = useRef(null);
  const canvasColorPickerRef = useRef(null);
  const {
    isPlaying,
    togglePlayback,
    requestAddItem,
    canvasBackgroundColor,
    setCanvasBackgroundColor,
  } = useCanvas();

  const handleToolClick = useCallback((tool) => {
    if (tool === 'ai') {
      setAiDialogOpen((prev) => !prev);
    } else {
      setAiDialogOpen(false);
      setIsCanvasColorPickerOpen(false);
      onToolChange?.(tool);
    }
  }, [onToolChange]);

  const isActive = (tool) => activeTool === tool;

  useEffect(() => {
    if (!isCanvasColorPickerOpen) return undefined;

    const updatePickerPosition = () => {
      const buttonRect = paintbrushButtonRef.current?.getBoundingClientRect();
      if (!buttonRect) return;

      const pickerWidth = 280;
      const maxLeft = window.innerWidth - pickerWidth - 8;
      const centeredLeft = buttonRect.left + (buttonRect.width / 2) - (pickerWidth / 2);
      const left = Math.max(8, Math.min(centeredLeft, maxLeft));
      const top = buttonRect.bottom + 8;

      setCanvasPickerPosition({ top, left });
    };

    updatePickerPosition();

    const handleClickOutside = (event) => {
      if (paintbrushButtonRef.current?.contains(event.target)) return;
      if (canvasColorPickerRef.current?.contains(event.target)) return;
      setIsCanvasColorPickerOpen(false);
    };

    window.addEventListener('resize', updatePickerPosition);
    window.addEventListener('scroll', updatePickerPosition, true);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('resize', updatePickerPosition);
      window.removeEventListener('scroll', updatePickerPosition, true);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCanvasColorPickerOpen]);

  return (
    <div className="relative flex items-center justify-center py-1">
      <div className="flex items-center gap-1 bg-ag-black-700 border border-ag-black-400 rounded-xl px-2 py-0.5">
        {/* Play/Pause button */}
        <button
          onClick={togglePlayback}
          className={`p-2 rounded-lg transition-colors ${isPlaying
              ? 'bg-ag-black-500'
              : 'hover:bg-ag-black-500/50'
            }`}
          title={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? (
            <Pause size={18} className="text-ag-green" />
          ) : (
            <Play size={18} className="text-[#9A9A9A] hover:text-white transition-colors" />
          )}
        </button>

        {/* Subtle divider */}
        <div className="w-px h-5 bg-ag-black-400 mx-0.5" />

        {/* Selector tool */}
        <button
          onClick={() => handleToolClick('selector')}
          className={`p-2 rounded-lg transition-colors ${isActive('selector')
              ? 'bg-ag-black-500'
              : 'hover:bg-ag-black-500/50'
            }`}
          title="Selector"
        >
          <SelectorIcon
            className={`w-[18px] h-[18px] ${isActive('selector')
                ? '[&_path]:fill-ag-black-300 [&_path]:stroke-ag-black-300'
                : '[&_path]:fill-ag-black-800 [&_path]:stroke-[#9A9A9A] hover:[&_path]:stroke-white'
              }`}
          />
        </button>

        {/* Canvas background color tool */}
        <div className="relative">
          <button
            ref={paintbrushButtonRef}
            onClick={() => setIsCanvasColorPickerOpen((prev) => !prev)}
            className="p-2 rounded-lg transition-colors hover:bg-ag-black-500/50"
            title="Canvas Background"
          >
            <Paintbrush size={18} className="text-[#9A9A9A] hover:text-white transition-colors" />
          </button>
        </div>

        {/* AI tool */}
        <button
          ref={aiButtonRef}
          onClick={() => handleToolClick('ai')}
          className={`p-2 rounded-lg transition-colors ${aiDialogOpen
              ? 'bg-ag-black-500'
              : 'hover:bg-ag-black-500/50'
            }`}
          title="AI Assistant"
        >
          <AiStarIcon
            className={`w-[18px] h-[18px] ${aiDialogOpen
                ? '[&_path]:fill-ag-green [&_path]:stroke-ag-green'
                : '[&_path]:fill-ag-black-800 [&_path]:stroke-[#9A9A9A] hover:[&_path]:stroke-white'
              }`}
          />
        </button>

        {/* Text tool */}
        <button
          onClick={() => {
            requestAddItem({
              id: `text_${Date.now()}`,
              type: 'text',
              name: 'Text',
              text: 'Your text here',
              fontFamily: 'Arial',
              fontSize: 48,
              color: '#ffffff',
              align: 'center',
              wordWrap: true,
              wordWrapWidth: 1600,
            });
            handleToolClick('text');
          }}
          className={`p-2 rounded-lg transition-colors ${isActive('text')
              ? 'bg-ag-black-500'
              : 'hover:bg-ag-black-500/50'
            }`}
          title="Text"
        >
          <TextIcon
            className={`w-[15px] h-[16px] ${isActive('text')
                ? '[&_path]:fill-ag-black-300 [&_path]:stroke-ag-black-300'
                : '[&_path]:fill-black [&_path]:stroke-[#9A9A9A] hover:[&_path]:stroke-white'
              }`}
          />
        </button>
      </div>

      {isCanvasColorPickerOpen && ReactDOM.createPortal(
        <div
          ref={canvasColorPickerRef}
          className="fixed z-[10000]"
          style={{
            top: `${canvasPickerPosition.top}px`,
            left: `${canvasPickerPosition.left}px`,
          }}
        >
          <AgColorPicker
            color={canvasBackgroundColor || '#000000'}
            onChange={(color) => setCanvasBackgroundColor(color)}
          />
        </div>,
        document.body
      )}

      {/* AI Dialog */}
      <AiDialog
        isOpen={aiDialogOpen}
        onClose={() => setAiDialogOpen(false)}
        anchorRef={aiButtonRef}
        onGenerate={(aiComponentData) => requestAddItem(aiComponentData)}
      />
    </div>
  );
};

export default CanvasTools;
